import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Building2 } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import { getUserOrganizations, createOrganization } from '../../services/organizationService';
import GettingStarted from './common/GettingStarted';

// Validation schema matching organization model
const organizationSchema = yup.object({
  name: yup
    .string()
    .required('Organization name is required')
    .min(1, 'Organization name must be at least 1 character')
    .max(100, 'Organization name must not exceed 100 characters')
    .trim(),
  description: yup
    .string()
    .max(500, 'Description must not exceed 500 characters')
    .trim()
    .nullable(),
  website: yup
    .string()
    .url('Please enter a valid URL (e.g., https://example.com)')
    .trim()
    .nullable(),
  industry: yup
    .string()
    .trim()
    .nullable(),
  size: yup
    .string()
    .oneOf(['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+', ''], 'Please select a valid company size')
    .nullable()
}).required();

const HomePage = () => {
  const { userInfo } = useUser();
  const history = useHistory();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(organizationSchema),
    defaultValues: {
      name: '',
      description: '',
      website: '',
      industry: '',
      size: ''
    }
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const resp = await getUserOrganizations();
      setOrganizations(resp.data || []);
    } catch (error) {
      // Error fetching organizations
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const getOrganizationInitial = (organizationName) => {
    return organizationName ? organizationName.charAt(0).toUpperCase() : 'O';
  };

  const truncateOrganizationName = (name, maxLength = 25) => {
    return name && name.length > maxLength 
      ? `${name.substring(0, maxLength)}...` 
      : name;
  };

  const handleCreateOrganization = async (data) => {
    setCreating(true);
    try {
      // Clean up data - remove empty strings
      const cleanData = {
        name: data.name.trim(),
        ...(data.description && { description: data.description.trim() }),
        ...(data.website && { website: data.website.trim() }),
        ...(data.industry && { industry: data.industry.trim() }),
        ...(data.size && { size: data.size })
      };

      const response = await createOrganization(cleanData);
      
      // Refresh organization list
      await fetchOrganizations();
      
      // Close modal and reset form
      setShowCreateModal(false);
      reset();
      
      // Redirect to new organization
      if (response.data.id || response.data._id) {
        history.push(`/organization/${response.data.id || response.data._id}`);
      }
    } catch (error) {
      alert('Error creating organization');
      alert(error.response?.data?.message || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading your organizations...</p>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Two-column layout: Left half for orgs, Right half for quickstart */}
      <div className="flex min-h-screen">
        {/* Left Half - Organizations */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          {/* Header - matching right side style */}
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#CC457D]/10 flex items-center justify-center">
                  <Building2 size={18} className="text-[#CC457D]" />
                </div>
                <div>
                  <h1 className="text-sm font-semibold text-gray-900">
                    Welcome back, {userInfo?.firstName || 'User'}
                  </h1>
                  <p className="text-xs text-gray-500">
                    {organizations.length > 0 
                      ? `${organizations.length} ${organizations.length === 1 ? 'organization' : 'organizations'} available`
                      : 'Create your first organization to get started'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#CC457D] text-white text-xs font-medium rounded-lg hover:bg-[#CC457D]/90 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Organization
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            <div className="max-w-xl">

            {/* Organizations List */}
            {organizations.length > 0 ? (
              <div className="space-y-2">
                {organizations.map((organization) => {
                  const orgName = organization.name || organization.agency_name || organization.organization_name || 'Unknown Organization';
                  const orgId = organization._id || organization.id;
                  
                  return (
                    <Link
                      key={orgId}
                      to={`/organization/${orgId}`}
                      className="group block"
                    >
                      <div className="p-4 rounded-lg border border-gray-100 transition-all duration-200 group-hover:bg-gray-50 group-hover:border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-lg bg-[#CC457D]/10 text-[#CC457D] flex items-center justify-center font-semibold text-sm">
                              {getOrganizationInitial(orgName)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 
                              className="text-sm font-medium text-gray-900 truncate"
                              title={orgName}
                            >
                              {truncateOrganizationName(orgName)}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {organization.role || organization.user_role || 'Member'}
                            </p>
                          </div>
                          <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 rounded-lg border border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
                  </svg>
                </div>
                <p className="text-gray-500 text-xs">
                  Click "New Organization" to get started
                </p>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Right Half - Quickstart Guide */}
        <div className="hidden lg:block w-1/2 max-w-xl border-l border-gray-100 bg-gray-50/50">
          <GettingStarted />
        </div>
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
              onClick={() => !creating && setShowCreateModal(false)}
            />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Create Organization</h2>
                <button
                  onClick={() => !creating && setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={creating}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit(handleCreateOrganization)} className="space-y-4">
                {/* Organization Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('name')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#CC457D] focus:border-transparent ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Acme Inc."
                    disabled={creating}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                    <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  <textarea
                    {...register('description')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#CC457D] focus:border-transparent ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="What does your organization do?"
                    rows={3}
                    disabled={creating}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                    <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  <input
                    type="url"
                    {...register('website')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#CC457D] focus:border-transparent ${
                      errors.website ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="https://example.com"
                    disabled={creating}
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
                  )}
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                    <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  <input
                    type="text"
                    {...register('industry')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#CC457D] focus:border-transparent"
                    placeholder="Technology, Healthcare, etc."
                    disabled={creating}
                  />
                </div>

                {/* Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Size
                    <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  <select
                    {...register('size')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#CC457D] focus:border-transparent ${
                      errors.size ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={creating}
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                  {errors.size && (
                    <p className="mt-1 text-sm text-red-600">{errors.size.message}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      reset();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-[#CC457D] text-white rounded-md hover:bg-[#CC457D]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    disabled={creating}
                  >
                    {creating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      'Create Organization'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
